import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { logout } from '@/shared/api/auth';
import { reloadAt } from '@/shared/lib/navigation';
import type { User } from '@/shared/api/types';
import { Avatar } from '@/shared/ui';

import styles from './Header.module.css';

/** Avatar and handle that open the account menu, in the manner of GitHub. */
export function UserMenu({ user }: { user: User }): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const signOut = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      queryClient.clear();
      reloadAt('/');
    },
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    const onPointer = (event: PointerEvent): void => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const close = (): void => {
    setOpen(false);
  };

  return (
    <div className={styles.menu} ref={root}>
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={t('menu.open')}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        <Avatar avatar={user.avatar} name={user.username} size={32} />
        <span className={styles.handle}>@{user.username}</span>
      </button>
      {open && (
        <div className={styles.dropdown} id={menuId} role="menu">
          <p className={styles.signedIn}>
            {t('menu.signedInAs')}
            <strong>{user.display_name || `@${user.username}`}</strong>
          </p>
          <Link className={styles.item} role="menuitem" to="/decks" onClick={close}>
            {t('menu.decks')}
          </Link>
          <Link className={styles.item} role="menuitem" to="/settings" onClick={close}>
            {t('menu.settings')}
          </Link>
          <button
            type="button"
            className={styles.item}
            role="menuitem"
            disabled={signOut.isPending}
            onClick={() => {
              signOut.mutate();
            }}
          >
            {t('menu.logout')}
          </button>
        </div>
      )}
    </div>
  );
}
